import axios from 'axios';
import config from '../temp/config';
import { isAbsoluteUri, getLangFromUrl } from '@/utils/uri-utils';
import { isFn, isNoE } from '@/utils/obj-utils';
import { merge } from 'lodash';
import { APP_CONST, REQUEST_TYPES } from '@/utils/constants';
import Md5 from 'crypto-js/md5';
import { webStorage } from '@/utils/web-storage';
import { S_LANG, S_LOGIN_INFO, S_LOGIN_URL } from '@/utils/web-storage-keys';
import { eq } from '@/utils/string-utils';
import { canUseDOM } from '@/utils/dom-utils';
import useAppStore from '@/store/appStore';
import { readMyAccountAuth } from '@/utils/myaccount-auth-reader';
const service = axios.create();
const requestPromises = {};

/** @description web api */
export const r = (path, loginRequired = false, hooks = null) => {
  return handleRequest(path, loginRequired, hooks, REQUEST_TYPES.web);
};
/** @description google map api */
export const gm = (path) => {
  return handleRequest(path, false, null, REQUEST_TYPES.gm);
};
export const handleRequest = (path, loginRequired = false, hooks, requestType) => {
  return (params = null, data = null, opts = {}) => {
    // const extraOpts = merge({}, _optsHandler ? _optsHandler(path, params, data) : null, opts);
    const options = getRequestOptions(path, loginRequired, params, data, opts, hooks, requestType);
    return processRequest(options, requestType);
  };
};
export const getRequestOptions = (path, loginRequired, params, data, opts, hooks, requestType) => {
  let [method, url] = path.split(' ');
  const loginInfo = webStorage.get(S_LOGIN_INFO);
  const myAccountAuth = readMyAccountAuth();
  const appStore = useAppStore();
  const site = appStore.site;
  const lang = appStore.lang || (canUseDOM() ? getLangFromUrl() : APP_CONST.lang);
  if (!params) params = {};
  if (isFn(hooks?.beforeBuildOptions)) {
    hooks.beforeBuildOptions(method, url, params, data);
  }
  url = url.replace(/\{.+?\}/gi, (match) => {
    const matchKey = match.replace(/\{|\}/gi, '');
    let result = match;
    if (params && params[matchKey]) {
      result = params[matchKey];
      delete params[matchKey];
    }
    if (data && data[matchKey]) {
      result = data[matchKey];
      delete data[matchKey];
    }
    return result;
  });
  // Remove timestamp params if cache busting is needed.
  // if (/^GET$/i.test(method)) {
  //   params = merge(params, {
  //     __t: `${new Date().getTime()}_${Math.floor(Math.random() * 1000)}`
  //   });
  // }
  if (!isAbsoluteUri(url)) {
    if (canUseDOM()) {
      url = `${window.location.origin}/${url}`;
    } else {
      // Use configured API host during SSR.
      url = `${config.sitecoreApiHost}/${url}`;
    }
  }
  const options = {
    method,
    url,
    params,
    data,
  };
  if (requestType !== REQUEST_TYPES.gm) {
    merge(options, {
      headers: {
        lang,
        site,
      },
    });
  }
  const { token } = loginInfo ?? {};
  const myAccountToken = myAccountAuth?.isLoggedIn ? myAccountAuth.token : '';
  if (!isNoE(myAccountToken)) {
    merge(options, {
      headers: {
        authorization: 'Bearer ' + myAccountToken,
      },
    });
  }
  if (loginRequired && !isNoE(token)) {
    merge(options, {
      headers: {
        authorization: 'Bearer ' + myAccountToken || token,
        token,
      },
    });
  }
  merge(options, opts);
  if (isFn(hooks?.afterBuildOptions)) {
    hooks.afterBuildOptions(options);
  }
  return options;
};
export const processRequest = (options, requestType) => {
  const requestKey = getRequestKey(options);
  if (!requestPromises[requestKey]) {
    requestPromises[requestKey] = new Promise((resolve) => {
      service
        .request(options)
        .then((res) => {
          if (res.status === 200) {
            resolve(handleResponse[requestType](res.data));
          } else {
            resolve([null, res]);
          }
        })
        .catch((ex) => {
          resolve([null, ex]);
        })
        .finally(() => {
          delete requestPromises[requestKey];
        });
    });
  }
  return requestPromises[requestKey];
};
export const handleResponse = {
  [REQUEST_TYPES.web](res) {
    const success = [0, '0', 10000000, '10000000'].includes(res.code) || res.status === 'Success';

    if (success) {
      return [res.result ?? res.data ?? null, null];
    } else {
      return [null, res];
    }
  },
  [REQUEST_TYPES.gm](res) {
    if (['SUCCESS', 'OK'].includes(res.status)) {
      return [res, null];
    } else {
      return [null, res];
    }
  },
};
const getRequestKey = (options) => {
  try {
    return Md5(
      JSON.stringify(options, (key, value) => {
        if (['computed', 'effect'].includes(key)) return null;
        return value;
      })
    ).toString();
  } catch (ex) {
    return null;
  }
};
